import React, { useContext, useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Card, Collapse, message, Empty,
} from 'antd';
import PanelContent from './PanelContent';
import Header from './Header';
import { AuthContext } from '../../../appContext';
import {
  GET_ALL_COHORT, GET_USER_COHORT, GET_USER_SUBJECT,
} from '../../../graphql';
import ViewSpinner from '../../../components/viewSpinner/ViewSpinner';
import './styles.less';

const { Panel } = Collapse;
const Home = () => {
  const { userInfo: { role } } = useContext(AuthContext);
  const isAdmin = role === 'ADMIN';

  /**
   * An administrator sees every cohort, and `cohorts` already carries each
   * one's subjects — so that is one request rather than two. Everyone else
   * sees what they are enrolled in, which is two scoped queries taking no
   * arguments: the previous pair took a user id and so could be pointed at
   * anyone's enrolments.
   */
  const { data: cohortPayload, loading: cohortLoading, error: cohortError } = useQuery(
    isAdmin ? GET_ALL_COHORT : GET_USER_COHORT,
  );
  const {
    data: subjectPayload, loading: subjectLoading, error: subjectError,
  } = useQuery(GET_USER_SUBJECT, { skip: isAdmin });

  const [cohorts, setCohorts] = useState(null);
  const [subjects, setSubjects] = useState(null);
  const [cohortWithSubject, setCohortWithSubject] = useState([]);

  useEffect(() => {
    if (cohortLoading || !cohortPayload) return;
    setCohorts(isAdmin ? cohortPayload.cohorts : cohortPayload.me.cohorts);
  }, [cohortPayload, cohortLoading, isAdmin]);

  useEffect(() => {
    if (isAdmin) {
      // Already nested under each cohort.
      if (!cohortLoading && cohortPayload) {
        setSubjects(cohortPayload.cohorts.flatMap(({ id, subjects: own }) => own.map(
          (subject) => ({ ...subject, cohortId: subject.cohortId ?? id }),
        )));
      }
      return;
    }
    if (!subjectLoading && subjectPayload) setSubjects(subjectPayload.mySubjects);
  }, [subjectPayload, subjectLoading, cohortPayload, cohortLoading, isAdmin]);

  useEffect(() => {
    if (subjects && cohorts) {
      let cohortWithSubjectArr = [];
      cohorts.forEach((cohort) => {
        const availSubject = subjects.filter(({ cohortId }) => cohortId === cohort.id);
        cohortWithSubjectArr = [...cohortWithSubjectArr, { ...cohort, subjects: availSubject }];
      });
      setCohortWithSubject(cohortWithSubjectArr);
    }
  }, [subjects, cohorts]);

  useEffect(() => {
    // Either error may be set on its own, so the message is taken from
    // whichever one arrived — reading `cohortError.message` unconditionally
    // threw when only the subject query had failed.
    const failure = cohortError ?? subjectError;
    if (!failure) return;
    message.error(
      failure.message?.includes('Failed to fetch')
        ? 'Network error, check connection'
        : 'Error, something went wrong',
    );
  }, [cohortError, subjectError]);
  return (
    <div>
      <Card className="gx-card">
        <h4>Your Cohorts</h4>
        <p className="gx-m-0">Create and update learning content here</p>
      </Card>
      {cohortWithSubject.length > 0 && (
      <Collapse defaultActiveKey="0" className="gx-mb-5" accordion expandIconPosition="end">
        {cohortWithSubject.map(({ name, subjects: subs }, idx) => (
          <Panel className="site-collapse-custom-panel" header={<Header subjectNumber={subs.length} cohortName={name} />} key={idx}>
            <PanelContent subjects={subs} />
          </Panel>
        ))}
      </Collapse>
      )}
      {!(cohortLoading || subjectLoading) && cohortWithSubject.length === 0 && <Empty description="You don't have a cohort" />}
      {(cohortLoading || subjectLoading) && <ViewSpinner /> }
    </div>
  );
};

export default Home;
